
type GenericFunc = {
    (...args: any): any
}

export class ResultsController {

    _id: string;
    listeners: Record<string, (GenericFunc | GenericFunc[] | undefined)> = {};
    debug: boolean = false;
    verbose: boolean = false;

    constructor(debug: boolean = false, verbose: boolean = false) {
        this.debug = debug;
        this.verbose = verbose;
        this._id = `ResultsController-${Math.floor(Math.random() * 10000)}`;
    }

    getListenersCount(): number {
        return Object.keys(this.listeners).length;
    }

    /**
     * 
     * @param {string} elementId The id or class of the element.
     * @param {keyof HTMLElementEventMap} eventType 
     * @param {string} key The key added to the eventListenerId.
     */
    _getEventListenerId(elementId: string, eventType: keyof HTMLElementEventMap | string, key?: string): string {
        if (key) {
            return `${eventType}@${elementId}#${key}`;
        }
        return `${eventType}@${elementId}`;
    }

    /**
     * 
     * @param {string} elementId The id or class of the element.
     * @param {string} key The key added to the eventListenerId.
     * @returns {[HTMLElement, boolean] | null} Returns null on error.
     */
    _getElement(elementId: string, key?: string): [HTMLElement, boolean] | null {
        if (['#', '.'].includes(elementId[0])) elementId = elementId.substring(1);
        let elements: NodeListOf<Element> = document.querySelectorAll('#' + elementId);
        let elem: HTMLElement;
        let isClass = false;
        if (!elements || elements.length === 0 || elements.length !== 1) {
            // Try to check if the given ID is a class.
            if (elements.length === 0) {
                elements = document.querySelectorAll('.' + elementId);
                if (!elements || elements.length === 0) {
                    this.debug && console.warn(`No element with id or class, '${elementId}' was found !`);
                    return null;
                }
            }

            var index = 0;
            var keyUsedAsIndex = false;
            if (key) {
                if (!isNaN(parseInt(key))) {
                    index = parseInt(key);
                    keyUsedAsIndex = true;
                }
            }
            var msg = `Found elements of class '${elementId}', using element at index '${index}'`;
            if (!keyUsedAsIndex) msg += `.\n\tUse an id or add a (numerical) key to change this behaviour !`;
            this.debug && console.warn(msg);
            if (elements.length < index) {
                this.debug && console.warn("Parsed index is higher than the number of elements, defaulting to index '0'.");
                index = 0;
            }
            elem = elements.item(index) as HTMLElement;
            isClass = true;
        } else if (elements && elements.length === 1) elem = elements.item(0) as HTMLElement;
        return [elem, isClass];
    }

    /**
     * @param {string} elementClass The class of the elements.
     * @returns {HTMLElement[] | null} Returns null on error.
     */
    _getElements(elementClass: string): HTMLElement[] | null {
        let elems: HTMLCollection = document.getElementsByClassName(elementClass);

        if (elems.length <= 0) {
            this.debug && console.warn(`No elements with class, '${elementClass}' were found !`);
            return null;
        }
        let elemList: HTMLElement[] = [];
        for (let i = 0; i < elems.length; i++) {
            elemList.push(elems.item(i) as HTMLElement);
        }

        return elemList;
    }

    /**
     * 
     * @param {string} key Represents the key in the this.listeners object.
     * @returns {[string, string, keyof HTMLElementEventMap | string] | null} Returns null on error.
     */
    _getInfoFromKey(key: string): [string, string, keyof HTMLElementEventMap | string] | null {
        const regex = /(?<EventType>[a-z]*)@(?<ID>[a-z](?:[a-z0-9\-\_])+)#?(?<KEY>[a-z0-9]+)?/i;
        const result = key.match(regex);

        if (!result || !result.groups) {
            console.error(`No match of regex '${regex}' on key '${key}' !\n\t\tDoes the id start with a number ?`);
            return null;
        }

        const elemId = result.groups['ID'];
        const eventType = result.groups['EventType'] as keyof HTMLElementEventMap | string;
        const k = result.groups['KEY'];
        return [elemId, k, eventType];
    }

    /**
     * This handles adding an event listener, 
     * if the element isn't visible the listener won't be added by default.
     * 
     * @param {keyof HTMLElementEventMap} eventType 
     * @param {GenericFunc} listener 
     * @param {string} elementId The id or class of the element.
     * @param {string|undefined} key The key added to the eventListenerId.
     * @param {boolean} ignoreVisibility Whether to ignore the current visibility of the element.
     */
    addEventListener(
        eventType: keyof HTMLElementEventMap, listener: (...args: any) => any,
        elementId: string, key?: string,
        ignoreVisibility?: boolean) {

        let elemInfo = this._getElement(elementId, key);
        if (elemInfo === null) return;
        let [elem, isClass] = elemInfo;

        if (!ignoreVisibility && !elem.checkVisibility()) return;

        const evtListId = this._getEventListenerId(elementId, eventType, key);
        let entry: (GenericFunc | GenericFunc[] | undefined) = this.listeners[evtListId];
        let isArray = Array.isArray(entry);

        if (!isClass && !isArray) {
            if (entry) {
                this.debug && console.warn(`Already added a listener of type '${eventType}' to element with id '${elementId}' !\n\t Overwriting old listener !`);
                elem.removeEventListener(eventType, entry as GenericFunc);
            }
            this.listeners[evtListId] = listener;
        } else {
            if (entry) {
                if (key) {
                    this.debug && console.warn(`Found a key, overwriting existant entry for id '${evtListId}' !`);
                    elem.removeEventListener(eventType, entry as GenericFunc);
                    this.listeners[evtListId] = [listener];
                } else {
                    this.debug && console.warn(`Found existant entry(-ies) for '${evtListId}', appending current entry...`);
                    (this.listeners[evtListId] as GenericFunc[]).push(listener);
                }
            } else {
                this.debug && console.log(`No entry found for '${evtListId}', creating one...`);
                let evtListeners = [listener];
                this.listeners[evtListId] = evtListeners;
            }
        }
        elem.addEventListener(eventType, listener);

        this.debug || this.verbose && console.log(`Added '${evtListId}' !`);
    }

    /**
     * This handles adding an event listener on multiple elements of a same class, 
     * if the element aren't visible the listener won't be added by default.
     * 
     * @param {keyof HTMLElementEventMap} eventType 
     * @param {GenericFunc} listener 
     * @param {string} elementClass The class of the elements.
     * @param {boolean} overwrite If the event listeneners should overwrite existing entries.
     * @param {boolean} ignoreVisibility Whether to ignore the current visibility of the element.
     */
    addEventListeners(
        eventType: keyof HTMLElementEventMap, listener: (...args: any) => any,
        elementClass: string, overwrite: boolean = true, ignoreVisibility?: boolean) {

        let elemInfo = this._getElements(elementClass);
        if (elemInfo === null) return;
        let elems = elemInfo;

        for (let i = 0; i < elems.length; i++) {
            let elem = elems[i];

            if (!ignoreVisibility && !elem.checkVisibility()) return;
            elem.addEventListener(eventType, listener);
            const evtListId = this._getEventListenerId(elementClass, eventType, `${i}`);
            let entry: (GenericFunc | GenericFunc[] | undefined) = this.listeners[evtListId];
            if (entry) {
                if (overwrite) {
                    this.debug && console.warn(`Overwriting entry '${evtListId}' !`);
                    this.listeners[evtListId] = [listener];
                } else {
                    this.debug && console.warn(`Appending to entry '${evtListId}'...`);
                    (this.listeners[evtListId] as GenericFunc[]).push(listener);
                }
            } else {
                this.debug && console.log(`No entry found for '${evtListId}', creating one...`);
                let evtListeners = [listener];
                this.listeners[evtListId] = evtListeners;
            }
            this.debug || this.verbose && console.log(`Added '${evtListId}' !`);
        }
    }

    /**
     * This handles removing an event listener, 
     * if the element isn't visible the listener won't be removed by default.
     * 
     * @param {keyof HTMLElementEventMap | string} eventType 
     * @param {string} elementId The id or class of the element.
     * @param {string|undefined} key The key added to the eventListenerId.
     * @param {boolean} ignoreVisibility Whether to ignore the current visibility of the element.
     */
    removeEventListener(
        eventType: keyof HTMLElementEventMap | string, elementId: string,
        key?: string, ignoreVisibility?: boolean) {

        let elemInfo = this._getElement(elementId, key);
        const evtListId = this._getEventListenerId(elementId, eventType, key);

        if (elemInfo === null) {
            if (this.listeners[evtListId] !== undefined) {
                delete this.listeners[evtListId];
                this.debug && console.warn(`No element found for id '${elementId}' but the key '${evtListId}' exists in listener list.`);
            }
            return 0;
        }
        let [elem, isClass] = elemInfo;
        let nbRemoved = 0;

        if (!ignoreVisibility && !elem.checkVisibility()) return 0;


        let entry: (GenericFunc | GenericFunc[] | undefined) = this.listeners[evtListId];
        let isArray = Array.isArray(entry);

        if (!isClass && !isArray) {
            let evtList = entry as (GenericFunc | undefined);

            if (!evtList) {
                this.debug && console.warn(`There are no eventListener that matches '${evtListId}', did you call remove by accident ?`);
            } else {
                elem.removeEventListener(eventType, evtList);
                nbRemoved++;
            }
        } else {
            let evtListeners = entry as (GenericFunc[] | undefined);

            if (!evtListeners || evtListeners.length === 0) {
                this.debug && console.warn(`There are no eventListeners that match '${evtListId}', did you call remove by accident ?`);
            } else {
                var msg = "Warning ! Calling removeEventListener on a class removes EVERY eventListener of that class !";
                msg += `\n\tUse an id or a key to change this behaviour !`;
                if (!key) {
                    this.debug && console.warn(msg);
                    for (let i = 0; i < evtListeners.length; i++) {
                        let evtListener = evtListeners[i];
                        elem.removeEventListener(eventType, evtListener);
                        nbRemoved++;
                    }
                } else {
                    if (evtListeners.length > 1) {
                        let index = parseInt(key);
                        if (isNaN(index) || index < 0 || index >= evtListeners.length) {
                            console.warn(`Key '${key}' is not a number or out of bounds, removing all listeners...`);
                            for (let i = 0; i < evtListeners.length; i++) {
                                let evtListener = evtListeners[i];
                                elem.removeEventListener(eventType, evtListener);
                                nbRemoved++;
                            }
                        } else {
                            let evtListener = evtListeners[index];
                            elem.removeEventListener(eventType, evtListener);
                            nbRemoved++;
                        }
                    } else {
                        let evtListener = evtListeners[0];
                        elem.removeEventListener(eventType, evtListener);
                        nbRemoved++;
                    }
                }
            }
        }
        delete this.listeners[evtListId];

        this.debug || this.verbose && console.log(`Removed '${evtListId}' !`);
        return nbRemoved;
    }

    /**
     * This handles removing an event listener, 
     * if the element isn't visible the listener won't be removed by default.
     * 
     * @param {keyof HTMLElementEventMap | string} eventType 
     * @param {string} elementClass The class of the elements.
     * @param {string|undefined} key The key added to the eventListenerId.
     * @param {boolean} ignoreVisibility Whether to ignore the current visibility of the element.
     */
    removeEventListeners(
        eventType: keyof HTMLElementEventMap | string, elementClass: string, ignoreVisibility?: boolean) {

        let elemInfo = this._getElements(elementClass);
        if (elemInfo === null) return;
        let elems = elemInfo;

        let nbRemoved = 0;
        for (let i = 0; i < elems.length; i++) {
            let elem = elems[i];

            if (!ignoreVisibility && !elem.checkVisibility()) return 0;
            const evtListId = this._getEventListenerId(elementClass, eventType, `${i}`);
            let entry: (GenericFunc | GenericFunc[] | undefined) = this.listeners[evtListId];
            let isArray = Array.isArray(entry);

            if (!isArray) {
                let evtList = entry as (GenericFunc | undefined);

                if (!evtList) {
                    this.debug && console.warn(`There are no eventListener that matches '${evtListId}', did you call remove by accident ?`);
                } else {
                    elem.removeEventListener(eventType, evtList);
                    nbRemoved++;
                }
            } else {
                let evtListeners = entry as (GenericFunc[] | undefined);

                if (!evtListeners || evtListeners.length === 0) {
                    this.debug && console.warn(`There are no eventListeners that match '${evtListId}', did you call remove by accident ?`);
                } else {
                    if (evtListeners.length === 1) {
                        let evtListener = evtListeners[0];
                        elem.removeEventListener(eventType, evtListener);
                        nbRemoved++;
                    } else {
                        this.debug && console.warn(`Multiple listeners found for '${evtListId}'!\n\tRemoving all...`);
                        for (let i = 0; i < evtListeners.length; i++) {
                            let evtListener = evtListeners[i];
                            elem.removeEventListener(eventType, evtListener);
                            nbRemoved++;
                        }
                    }
                }
            }
            delete this.listeners[evtListId];
            this.debug || this.verbose && console.log(`Removed '${evtListId}' !`);
        }
        return nbRemoved;
    }

    destroy() {
        this.debug && console.log("Destroy called on controller !\n\tRemoving listeners...");

        let i = 0;
        let keys = Object.keys(this.listeners)
        if (keys.length !== 0) {
            keys.forEach((key) => {
                if (this.listeners[key] === undefined) {
                    this.verbose && console.warn(`Empty entry at key '${key}', skipping...`);
                    return;
                }
                let infos = this._getInfoFromKey(key);
                if (infos === null) return;
                let [elemId, keyString, eventType] = infos;
                i += this.removeEventListener(eventType, elemId, keyString, true);
                delete this.listeners[key];
            });
        }

        this.verbose && console.log(`Destroy finished.\n\tRemoved ${i} listener(s).`);
        this.listeners = {};
    }
}